import { useMemo } from 'react'

import { useTranslation } from 'react-i18next'

import config from '@app/config'
import { ConversationItem } from '@app/models'
import { getUserLanguage } from '@app/utils'

import { useLanguages } from '..'

export const useConversationLanguage = (
  conversation: ConversationItem | null | undefined
) => {
  const { languages } = useLanguages()
  const { i18n } = useTranslation()

  const userLanguage = useMemo(
    () => (i18n.language || getUserLanguage()).split('-')[0].toLowerCase(),
    [i18n.language]
  )

  const conversationLanguage = useMemo(() => {
    if (!conversation?.messages) {
      return undefined
    }

    return conversation.messages.find(
      (x) =>
        x.content?.language !== undefined &&
        x.content.language.toLowerCase() !== userLanguage
    )?.content?.language
  }, [conversation?.messages, userLanguage])

  const isTranslationEnabled = useMemo(() => {
    const languageExceptions = (
      config.regionalSettings?.languageExceptions ?? []
    ).map((language) => language.toLowerCase())

    return !!(
      conversationLanguage &&
      !languageExceptions.includes(conversationLanguage.toLowerCase())
    )
  }, [conversationLanguage, config.regionalSettings?.languageExceptions])

  const languageName = useMemo(() => {
    return languages.find((l) => l.id === conversationLanguage)?.name
  }, [conversationLanguage, languages])

  return { conversationLanguage, isTranslationEnabled, languageName }
}
